import { useState } from 'react';

import {
  AppIcon,
  DonutChart,
  DonutSegment,
  CareerTrackDistribution,
  useContainerQuery,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { WhatIsACareerTrackModalContent } from '@backrs_ui/grow-ui/src/pages/CareerTrack/WhatIsACareerTrackModalContent';

import { Box, Theme, Typography, SxProps, Dialog } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { useAgentContext } from './TrackDistribution.agentContext';
import { transformCareerTrackDistributionData } from '../util';

const STRINGS = appStrings.modules.careerPath.detailPage.trackDistribution;

export function TrackDistribution({
  careerTrackDistribution,
}: {
  careerTrackDistribution: CareerTrackDistribution;
}) {
  useAgentContext(careerTrackDistribution);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [containerRef, isCompact] = useContainerQuery(600);
  const transformedData = transformCareerTrackDistributionData(careerTrackDistribution);

  const donutSegments: DonutSegment[] = transformedData.map(({ label, value, color }) => ({
    label,
    value: value ?? 0,
    color,
  }));

  return (
    <Box ref={containerRef}>
      {/* content section container — chart left, description right */}
      <Box sx={contentSectionContainerSx}>
        {/* card with chart container */}
        <Box sx={chartCardContainerSx}>
          <Box sx={cardHeaderSx}>
            <Typography
              variant="text-sm-semibold"
              color={(theme: Theme) => theme.backrs.colors['Gray/900']}
            >
              {STRINGS.careerTrackDistribution}
            </Typography>
            <Box
              id="career-tracks-info-button"
              sx={{ cursor: 'pointer' }}
              onClick={() => setIsModalOpen(true)}
            >
              <AppIcon iconName="InfoCircle" color="Colors/Foreground/fg-quinary" diameter="16px" />
            </Box>
          </Box>
          <Box sx={cardBodySx}>
            <DonutChart
              variant={{ type: 'multi', segments: donutSegments }}
              size={isCompact ? 'small' : 200}
            />
            <Box sx={legendContainerSx}>
              {transformedData.map((tier) => (
                <Box key={tier.key} display="flex" flexDirection="column">
                  <Box sx={keyIconAndLabelSx}>
                    <AppIcon
                      iconName="Circle"
                      diameter="10px"
                      color="Colors/Base/Black"
                      fill={tier.color}
                    />
                    <Typography
                      variant="text-sm-medium"
                      color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
                    >
                      {tier.label}
                    </Typography>
                  </Box>
                  {tier.value && (
                    <Typography
                      color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-primary']}
                      variant="text-xs-semibold"
                      className="percentage-value"
                      sx={{ paddingLeft: '18px' }}
                    >
                      {STRINGS.tierPercentage(tier.value)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* description container — right side */}
        <Box sx={descriptionContainerSx}>
          <Typography variant="text-md-semibold" sx={descriptionTitleSx}>
            {STRINGS.careerTracks}
          </Typography>
          <Typography variant="text-sm-regular">
            <ReactMarkdown>{STRINGS.description}</ReactMarkdown>
          </Typography>
        </Box>
      </Box>

      <Dialog
        id="career-tracks-modal"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: dialogSx,
        }}
      >
        <WhatIsACareerTrackModalContent onClose={() => setIsModalOpen(false)} />
      </Dialog>
    </Box>
  );
}

const cardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  paddingY: backrs.spacing['spacing-lg'],
  paddingX: backrs.spacing['spacing-2xl'],
});

const cardBodySx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Gray/200']}`,
  borderRadius: backrs.radius['radius-xl'],
  padding: backrs.spacing['spacing-2xl'],
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'flex-start',
  justifyItems: 'center',
  '@container (min-width: 400px)': {
    gridTemplateColumns: '1fr 1fr',
    gap: backrs.spacing['spacing-6xl'],
    justifyItems: 'start',
  },
});

const legendContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const descriptionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const descriptionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/500'],
});

const contentSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-5xl'],
  alignItems: 'start',
  '@container (min-width: 760px)': {
    gridTemplateColumns: '1.3fr 1fr',
  },
});

const chartCardContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  maxWidth: '600px',
});

const keyIconAndLabelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-md'],
  alignItems: 'center',
});

const dialogSx: SxProps<Theme> = ({ backrs }) => ({
  maxWidth: '90vw',
  maxHeight: '90vh',
  borderRadius: backrs.radius['radius-2xl'],
});
